import React, { useState } from "react";
import withStyles from "isomorphic-style-loader/withStyles";
import phone from "phone";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import PrimaryButton from "../primaryButton";
import AdminOtpPanel from "../adminOtpPanel";
import { adminOtpChallenge, confirmAdminPhone } from "../../utils/fetch";
import s from "./styles.scss";

export interface AdminPhoneInlineFormProps {
  authServiceUrl: string;
  onComplete: () => void;
  submitLabel?: string;
  compact?: boolean;
  centered?: boolean;
}

function AdminPhoneInlineFormView({
  authServiceUrl,
  onComplete,
  submitLabel = "Add phone number",
  compact = false,
  centered = false,
}: AdminPhoneInlineFormProps) {
  const [phoneField, setPhoneField] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otpActive, setOtpActive] = useState(false);
  const [otpChallengeId, setOtpChallengeId] = useState<string | null>(null);

  const submitPhone = async () => {
    const raw = phoneField.trim();
    if (!raw) {
      setError("Enter a phone number.");
      return;
    }
    const parsed = phone(raw, "US");
    if (!parsed.isValid) {
      setError("Please enter a valid US phone number.");
      return;
    }
    const e164 = parsed.phoneNumber;
    const fbUser = firebase.auth().currentUser;
    if (!fbUser) {
      setError("Not signed in.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const firebaseToken = await fbUser.getIdToken(false);
      const ch = await adminOtpChallenge(authServiceUrl, {
        clientType: "web",
        operation: "phone",
        firebaseToken,
        newPhoneNumber: e164,
      });
      if (!ch.otpRequired && ch.completionToken) {
        await confirmAdminPhone(authServiceUrl, ch.completionToken);
        setLoading(false);
        onComplete();
        return;
      }
      if (ch.challengeId) {
        setOtpChallengeId(ch.challengeId);
        setOtpActive(true);
        setLoading(false);
        return;
      }
      throw new Error("Unexpected verification response.");
    } catch (e: any) {
      setLoading(false);
      setError(e?.message || String(e));
    }
  };

  const onOtpVerified = async (completionToken: string) => {
    setLoading(true);
    setError(null);
    try {
      await confirmAdminPhone(authServiceUrl, completionToken);
      setOtpActive(false);
      setOtpChallengeId(null);
      setLoading(false);
      onComplete();
    } catch (e: any) {
      setLoading(false);
      setError(e?.message || String(e));
      return Promise.reject(e);
    }
  };

  const backFromOtp = () => {
    setOtpActive(false);
    setOtpChallengeId(null);
    setError(null);
  };

  if (otpActive && otpChallengeId) {
    return (
      <div className={`${s.root} ${centered ? s.rootCentered : ""} ${s.otpWrap}`}>
        <AdminOtpPanel
          authServiceUrl={authServiceUrl}
          challengeId={otpChallengeId}
          forceSmsOnly
          initialChannel="sms"
          onVerified={onOtpVerified}
          onBack={backFromOtp}
          intro="Enter the code we sent to verify this phone number."
          showVerifiedSuccessScreen
          verifiedSuccessTitle="Phone number confirmed"
          verifiedSuccessBlurb=""
        />
        {error ? <div className={s.error}>{error}</div> : null}
      </div>
    );
  }

  return (
    <div className={`${s.root} ${centered ? s.rootCentered : ""}`}>
      <label className={s.label} htmlFor="adminInlinePhone">
        Phone number
      </label>
      <input
        id="adminInlinePhone"
        type="tel"
        autoComplete="tel"
        className={`${s.input} ${compact ? s.inputCompact : ""}`}
        value={phoneField}
        onChange={e => {
          setPhoneField(e.target.value);
          setError(null);
        }}
        disabled={loading}
        placeholder="(555) 555-5555"
      />
      {error ? <div className={s.error}>{error}</div> : null}
      <PrimaryButton
        type="button"
        className={`${s.submit} ${compact ? s.submitCompact : ""}`}
        onClick={() => void submitPhone()}
        disabled={loading}
        loading={loading}
        loadingText="Saving…"
      >
        {submitLabel}
      </PrimaryButton>
    </div>
  );
}

export default withStyles(s)(AdminPhoneInlineFormView);
